import { IResource } from "@gettactic/api";
import { useAuthenticated, useFloors } from "@gettactic/hooks";
import { Copy, X } from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { sanitizeMapValueForCopy } from "../../../../utils/copyMapValue";
import { EditorSelect } from "../../../ui/EditorSelect";
import { IconButton } from "../../../ui/IconButton";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import { MapCanvasValue } from "../../contexts/MapCanvasContext/MapCanvasContext.types";

/**
 * Right-side drawer to copy another floor's map into the current editor.
 * The copied shapes get fresh ids and spots lose their resource binding
 * (same reset the clipboard paste applies), so saving creates new
 * resources for this floor instead of sharing the source floor's.
 */
export const CopyMapFromFloor: React.FC = () => {
  const { loadMapValue, resourceId } = useMapCanvas();
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  const offices = authenticatedUser?.offices?.offices || [];
  const [open, setOpen] = React.useState(false);
  const [officeId, setOfficeId] = React.useState<string | null>(
    currentOffice?.id ?? null
  );
  const [confirmingFloorId, setConfirmingFloorId] = React.useState<
    string | null
  >(null);

  const { floors, loading } = useFloors(open ? officeId : null);

  // Only floors that actually have a builder map, excluding the floor
  // being edited.
  const copyableFloors = (floors || []).filter(
    (floor) => !!floor.map_data && floor.id !== resourceId
  );

  const handleCopy = (floor: IResource) => {
    try {
      const mapValue = JSON.parse(floor.map_data || "") as MapCanvasValue;
      loadMapValue(sanitizeMapValueForCopy(mapValue));
      toast.success(
        `Copied the map from "${floor.name}". Save to create this floor's resources.`
      );
      setOpen(false);
      setConfirmingFloorId(null);
    } catch {
      toast.error("That floor's map data could not be read");
    }
  };

  return (
    <>
      <IconButton
        label="Copy map from another floor"
        isActive={open}
        onClick={() => {
          setOpen((prev) => !prev);
          setConfirmingFloorId(null);
        }}
      >
        <Copy size={16} />
      </IconButton>

      {open && (
        <div className="absolute top-12 right-0 bottom-0 z-50 flex w-72 flex-col border-neutral-200 border-l bg-background-primary shadow-lg">
          <div className="flex h-10 flex-none items-center justify-between border-neutral-200 border-b py-2 pr-2 pl-4">
            <span className="font-semibold text-neutral-900 text-sm">
              Copy map from floor
            </span>
            <IconButton
              label="Close copy map"
              size="sm"
              onClick={() => setOpen(false)}
            >
              <X size={15} />
            </IconButton>
          </div>

          <div className="flex flex-col gap-2 border-neutral-200 border-b p-3">
            <span className="text-neutral-600 text-xs">Office</span>
            <EditorSelect
              label="Office"
              value={officeId || ""}
              onValueChange={(value) => {
                setOfficeId(value);
                setConfirmingFloorId(null);
              }}
              options={offices.map((office) => ({
                value: office.id,
                label: office.name
              }))}
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto py-1">
            {loading ? (
              <div className="px-4 py-8 text-center text-neutral-400 text-xs">
                Loading floors…
              </div>
            ) : copyableFloors.length === 0 ? (
              <div className="px-4 py-8 text-center text-neutral-400 text-xs">
                No other floors with a builder map in this office
              </div>
            ) : (
              copyableFloors.map((floor) => (
                <div
                  key={floor.id}
                  className="flex items-center gap-2 px-4 py-2 hover:bg-neutral-50"
                >
                  <span className="min-w-0 flex-1 truncate text-neutral-900 text-xs">
                    {floor.name}
                  </span>
                  {confirmingFloorId === floor.id ? (
                    <>
                      <button
                        type="button"
                        className="flex-none rounded-md bg-red-50 px-2 py-1 font-medium text-red-700 text-xs hover:bg-red-100"
                        onClick={() => handleCopy(floor)}
                      >
                        Replace map
                      </button>
                      <button
                        type="button"
                        className="flex-none rounded-md px-2 py-1 text-neutral-500 text-xs hover:bg-neutral-100"
                        onClick={() => setConfirmingFloorId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="flex-none rounded-md bg-blue-50 px-2 py-1 font-medium text-blue-700 text-xs hover:bg-blue-100"
                      onClick={() => setConfirmingFloorId(floor.id)}
                    >
                      Copy
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <p className="border-neutral-200 border-t p-3 text-neutral-400 text-xs">
            Copying replaces the current canvas. Spots are unlinked from their
            resources — new ones are created when you save.
          </p>
        </div>
      )}
    </>
  );
};
